import { Check, LogOut } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { InvestorProfileReview } from './components/investor-profile-review.js'
import { OnboardingBrandPanel } from './components/onboarding-brand-panel.js'
import { OnboardingForm } from './components/onboarding-form.js'
import { OnboardingLayout } from './components/onboarding-layout.js'
import type { ProcessingTask } from './components/processing-screen.js'
import { ProcessingScreen } from './components/processing-screen.js'
import { ProfileReview } from './components/profile-review.js'
import { SignupQuestionCard } from './components/signup-question-card.js'
import type {
  ExtractedInvestorData,
  ExtractedStartupData,
  LinkedInProfile,
  OnboardingApi,
  WebsiteData,
} from './lib/api.js'
import { type SignupQuestionSource, useSignupQuestion } from './lib/use-signup-question.js'

type FlowStep = 'form' | 'processing' | 'profile'

export interface OnboardingCompleteResult {
  userId: string
  startupId?: string
  investorId?: string
}

export interface OnboardingFlowProps {
  onComplete: (result: OnboardingCompleteResult) => void
  api: OnboardingApi
  sendOtp: (email: string) => Promise<void>
  verifyOtp: (email: string, otp: string) => Promise<boolean>
  /** Starts the Google redirect flow. Omit to hide the button. */
  onGoogleSignIn?: () => Promise<{ error?: unknown } | undefined>
  /** A one-off banner message to show on mount, e.g. a decoded Google sign-in failure. */
  notice?: string
  /** When true, skip email/OTP steps (user is already authenticated) */
  authenticated?: boolean
  /** Pre-fill email when authenticated */
  userEmail?: string
  /** The signed-in user's name (e.g. from Google) — used only as a fallback
   * to pre-fill the profile-review name field when there's no LinkedIn scrape
   * to name them from; the founder/investor can still edit it. */
  userName?: string
  /** Pre-fill role when already set */
  userRole?: 'founder' | 'investor' | null
  /** Called when authenticated user clicks email to sign out */
  onSignOut?: () => void
  /** Invite token — when set, user is joining an existing startup (skip startup creation) */
  inviteToken?: string
  /** Pre-filled email from invite lookup */
  inviteEmail?: string
  /** Role inferred from invite (founder for startup invites, investor for investor invites) */
  inviteRole?: 'founder' | 'investor'
  /** Available investment area options for investor onboarding */
  areaOptions?: { value: string; label: string }[]
  /** Available investment region options for investor onboarding */
  regionOptions?: { value: string; label: string }[]
  /** Available product stage options for founder profile review */
  stageOptions?: { value: string; label: string }[]
  /** Available raise stage options for founder profile review */
  raiseStageOptions?: { value: string; label: string }[]
  /** Available category options for founder profile review */
  categoryOptions?: { value: string; label: string }[]
  /** Pre-selected pitch deck file (e.g. from public investor page). Skips role + document steps. */
  pendingPitchFile?: File
  /** Investor ID when onboarding from investor's public page */
  investorId?: string
  /** Custom left panel content for the form step (replaces default branding) */
  leftPanel?: React.ReactNode
  /** Custom title for the email step */
  formTitle?: string
  /** Custom subtitle for the email step */
  formSubtitle?: string
  /**
   * "How did you first hear about SeedForge?", asked while the profile is
   * analysed. Omit it to not ask; invites never see it.
   */
  signupQuestion?: SignupQuestionSource
  /**
   * Shown in place of the flow until the caller removes it. A signed-in account that has not accepted the terms
   * is kept on this step, before its role is picked.
   */
  termsStep?: React.ReactNode
}

interface OnboardingData {
  email: string
  linkedinUrl: string | null
  websiteUrl: string | null
  documentId: string | null
  documentName: string | null
  role: 'founder' | 'investor'
}

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

export function OnboardingFlow({
  onComplete,
  api,
  sendOtp,
  verifyOtp,
  onGoogleSignIn,
  notice,
  authenticated,
  userEmail,
  userName,
  userRole,
  onSignOut,
  inviteToken,
  inviteEmail,
  inviteRole,
  areaOptions,
  regionOptions,
  stageOptions,
  raiseStageOptions,
  categoryOptions,
  pendingPitchFile,
  investorId,
  leftPanel,
  formTitle,
  formSubtitle,
  signupQuestion,
  termsStep,
}: OnboardingFlowProps) {
  const isInviteFlow = !!inviteToken
  const [step, setStep] = useState<FlowStep>('form')
  const [data, setData] = useState<OnboardingData>({
    email: '',
    linkedinUrl: null,
    websiteUrl: null,
    documentId: null,
    documentName: null,
    role: 'founder',
  })
  const [error, setError] = useState<string | null>(null)

  // Results from processing phase, used by profile review
  const linkedInProfileRef = useRef<LinkedInProfile | null>(null)
  const extractedDataRef = useRef<ExtractedStartupData | null>(null)
  const websiteDataRef = useRef<WebsiteData | null>(null)
  const extractedInvestorDataRef = useRef<ExtractedInvestorData | null>(null)
  const completeResultRef = useRef<OnboardingCompleteResult | null>(null)

  // ── Form submitted → upload pending file if any, then go to processing ──

  const handleFormSubmit = useCallback(
    async (formData: {
      email: string
      linkedinUrl: string | null
      websiteUrl: string | null
      documentId: string | null
      documentName: string | null
      role: 'founder' | 'investor'
    }) => {
      if (pendingPitchFile) {
        try {
          await api.setRole(formData.role)

          if (!formData.documentId) {
            const doc = await api.createDocument(pendingPitchFile.name)
            await api.uploadToPresignedUrl(doc.uploadUrl, doc.uploadFields, pendingPitchFile)
            await api.completeDocumentUpload(doc.id)
            formData.documentId = doc.id
            formData.documentName = pendingPitchFile.name
          }
        } catch (err) {
          setError(
            err instanceof Error ? err.message : 'Failed to upload pitch deck. Please try again.'
          )
          return
        }
      }

      setData({ ...formData })
      setStep('processing')
    },
    [api, pendingPitchFile]
  )

  // ── Build processing tasks dynamically based on collected data ──

  const buildProcessingTasks = useCallback(
    (formData: OnboardingData): ProcessingTask[] => {
      if (isInviteFlow) {
        // Invite flow: exactly 2 visible steps
        return [
          {
            label: 'Preparing your profile',
            run: async () => {
              // Scrape LinkedIn if provided
              if (formData.linkedinUrl) {
                try {
                  const profile = await api.scrapeLinkedIn(formData.linkedinUrl)
                  linkedInProfileRef.current = profile
                } catch {
                  linkedInProfileRef.current = null
                }
              }
              // Save user profile
              await api.updateUser({
                name: linkedInProfileRef.current?.name,
                linkedinUrl: linkedInProfileRef.current?.linkedinUrl ?? formData.linkedinUrl,
                image: linkedInProfileRef.current?.profileImage,
                location: linkedInProfileRef.current?.location,
              })
            },
          },
          {
            label: 'Joining the team',
            run: async () => {
              const invite = await api.acceptInvite(inviteToken as string)
              await api.completeOnboarding()
              completeResultRef.current = {
                userId: '',
                startupId: invite.startupId ?? undefined,
                investorId: invite.investorId ?? undefined,
              }
            },
          },
        ]
      }

      // Regular flow: scrape + extract as separate visible steps, then a "preparing" placeholder
      const tasks: ProcessingTask[] = []

      if (formData.linkedinUrl) {
        tasks.push({
          label: 'Analyzing your LinkedIn profile',
          run: async () => {
            try {
              const profile = await api.scrapeLinkedIn(formData.linkedinUrl as string)
              linkedInProfileRef.current = profile
            } catch {
              linkedInProfileRef.current = null
            }
          },
        })
      }

      if (formData.websiteUrl) {
        tasks.push({
          label: 'Analyzing website',
          run: async () => {
            try {
              const data = await api.scrapeWebsite(formData.websiteUrl as string, formData.role)
              websiteDataRef.current = data
            } catch {
              websiteDataRef.current = null
            }
          },
        })
      }

      if (formData.documentId && formData.role !== 'investor') {
        tasks.push({
          label: 'Processing your pitch deck',
          run: async () => {
            try {
              const extracted = await api.extractDeck(formData.documentId as string)
              extractedDataRef.current = extracted
            } catch {
              extractedDataRef.current = null
            }
          },
        })
      }

      tasks.push({
        label: 'Preparing your profile',
        run: () => delay(500),
      })

      return tasks
    },
    [api, isInviteFlow, inviteToken]
  )

  // ── Processing complete ──

  const question = useSignupQuestion(
    isInviteFlow ? undefined : signupQuestion,
    step === 'processing'
  )

  const handleProcessingComplete = useCallback(() => {
    question.save(data.role)
    if (isInviteFlow && completeResultRef.current) {
      onComplete(completeResultRef.current)
    } else {
      setStep('profile')
    }
  }, [isInviteFlow, onComplete, question.save, data.role])

  // ── Profile confirmed → save and go to dashboard ──

  const handleProfileConfirm = useCallback(
    async (profileData: {
      name: string
      description: string
      location: string
      websiteUrl: string
      categoryId: string
      tags: string[]
      productStage: string
      foundingDate: string
      isFundraising: boolean
      fundraisingAmount: string
      fundraisingStage: string
      documents: { documentId: string; type?: string }[]
      teamMembers: { name: string; position?: string; linkedinUrl?: string | null }[]
    }) => {
      setError(null)

      try {
        // The reviewed founder name, then the LinkedIn scrape. Never the company
        // name — a startup is not a person.
        await api.updateUser({
          name: profileData.teamMembers[0]?.name ?? linkedInProfileRef.current?.name,
          linkedinUrl: linkedInProfileRef.current?.linkedinUrl ?? data.linkedinUrl,
          image: linkedInProfileRef.current?.profileImage,
          location: profileData.location || linkedInProfileRef.current?.location,
        })

        // Build unified documents array: pitch deck from form + additional from profile review
        const documents: { documentId: string; type?: string }[] = []
        if (data.documentId) {
          documents.push({ documentId: data.documentId, type: 'PITCH_DECK' })
        }
        documents.push(...profileData.documents)

        const startup = await api.createStartup({
          name: profileData.name,
          description: profileData.description,
          location: profileData.location || undefined,
          websiteUrl: profileData.websiteUrl || undefined,
          categoryId: profileData.categoryId || undefined,
          tags: profileData.tags,
          productStage: profileData.productStage || undefined,
          foundingDate: profileData.foundingDate || undefined,
          isFundraising: profileData.isFundraising,
          fundraisingAmount: profileData.fundraisingAmount || undefined,
          fundraisingStage: profileData.fundraisingStage || undefined,
          teamMembers: profileData.teamMembers,
        })

        // Link documents to the startup. Linking the pitch deck triggers the
        // prescreen server-side, so it no longer depends on the browser staying
        // on this page. An investorId targets an investor-specific prescreen.
        if (documents.length) {
          await Promise.all(
            documents.map((doc) =>
              api.linkDocument(startup.id, doc.documentId, {
                type: doc.type,
                label: doc.type === 'PITCH_DECK' ? 'Pitch Deck' : undefined,
                investorId: doc.type === 'PITCH_DECK' ? investorId : undefined,
              })
            )
          )
        }

        await api.completeOnboarding()
        onComplete({ userId: '', startupId: startup.id })
      } catch (err) {
        console.error('Failed to complete onboarding:', err)
        setError(err instanceof Error ? err.message : 'Something went wrong')
      }
    },
    [api, data, onComplete, investorId]
  )

  // ── Investor profile confirmed → save and go to dashboard ──

  const handleInvestorProfileConfirm = useCallback(
    async (profileData: {
      fundName: string
      fundWebsite: string
      description: string
      investmentAreas: string[]
      investmentGeographies: string[]
      userName: string
      position: string
    }) => {
      setError(null)

      try {
        await api.updateUser({
          name: profileData.userName || linkedInProfileRef.current?.name,
          linkedinUrl: linkedInProfileRef.current?.linkedinUrl ?? data.linkedinUrl,
          image: linkedInProfileRef.current?.profileImage,
          location: linkedInProfileRef.current?.location,
        })

        const investor = await api.createInvestor({
          name: profileData.fundName,
          websiteUrl: profileData.fundWebsite || undefined,
        })

        await api.updateInvestor(investor.id, {
          description: profileData.description || undefined,
          investmentAreas: profileData.investmentAreas,
          investmentGeographies: profileData.investmentGeographies,
          thesisDocumentId: data.documentId,
        })

        await api.completeOnboarding()
        onComplete({ userId: '', investorId: investor.id })
      } catch (err) {
        console.error('Failed to complete onboarding:', err)
        setError(err instanceof Error ? err.message : 'Something went wrong')
      }
    },
    [api, data, onComplete]
  )

  if (termsStep) {
    return (
      <OnboardingLayout variant="raw">
        <OnboardingBrandPanel audience={null} />
        <div className="flex flex-1 items-center justify-center p-8">
          <div className="w-full max-w-sm space-y-6">
            {userEmail && (
              <div className="flex w-full items-center justify-center gap-2 rounded-lg py-2">
                <Check className="size-4 shrink-0 text-emerald-500" />
                <span className="truncate text-sm font-medium text-foreground">{userEmail}</span>
                {onSignOut && (
                  <button
                    type="button"
                    aria-label="Sign out"
                    onClick={onSignOut}
                    className="ml-1 rounded-md p-1 text-muted-foreground transition-colors hover:bg-white/[0.06] hover:text-foreground"
                  >
                    <LogOut className="size-3.5" />
                  </button>
                )}
              </div>
            )}
            {termsStep}
          </div>
        </div>
      </OnboardingLayout>
    )
  }

  switch (step) {
    case 'form':
      return (
        <OnboardingForm
          api={api}
          sendOtp={sendOtp}
          verifyOtp={verifyOtp}
          onGoogleSignIn={onGoogleSignIn}
          notice={notice}
          onSubmit={handleFormSubmit}
          authenticated={authenticated}
          userEmail={userEmail}
          userRole={pendingPitchFile ? 'founder' : userRole}
          onSignOut={onSignOut}
          inviteToken={inviteToken}
          inviteEmail={inviteEmail}
          inviteRole={inviteRole}
          skipDocumentStep={!!pendingPitchFile}
          leftPanel={leftPanel}
          formTitle={isInviteFlow ? "You've been invited" : formTitle}
          formSubtitle={isInviteFlow ? 'Verify your email to join the team' : formSubtitle}
        />
      )
    case 'processing':
      return (
        <ProcessingScreen
          title={isInviteFlow ? 'Joining the team' : 'Analyzing your profile'}
          subtitle={
            isInviteFlow ? "We're setting up your workspace" : "We're gathering everything we need"
          }
          estimate={isInviteFlow ? undefined : 'about a minute'}
          tasks={buildProcessingTasks(data)}
          onComplete={handleProcessingComplete}
          onError={(errorMsg: string) => {
            setError(errorMsg)
            setStep('form')
          }}
          waitForContinue={question.config !== null}
        >
          {question.config ? (
            <SignupQuestionCard
              config={question.config}
              audience={data.role}
              onChange={(answer) => {
                question.answer.current = answer
              }}
            />
          ) : null}
        </ProcessingScreen>
      )
    case 'profile':
      if (data.role === 'investor') {
        return (
          <InvestorProfileReview
            linkedInProfile={linkedInProfileRef.current}
            websiteData={websiteDataRef.current}
            extractedData={extractedInvestorDataRef.current}
            websiteUrl={data.websiteUrl}
            defaultUserName={userName}
            error={error}
            areaOptions={areaOptions}
            regionOptions={regionOptions}
            onConfirm={handleInvestorProfileConfirm}
          />
        )
      }
      return (
        <ProfileReview
          linkedInProfile={linkedInProfileRef.current}
          extractedData={extractedDataRef.current}
          websiteData={websiteDataRef.current}
          websiteUrl={data.websiteUrl}
          userName={userName}
          error={error}
          stageOptions={stageOptions}
          raiseStageOptions={raiseStageOptions}
          categoryOptions={categoryOptions}
          onConfirm={handleProfileConfirm}
          onUploadFiles={async (files) => {
            const results = await Promise.all(
              files.map(async (file) => {
                const doc = await api.createDocument(file.name)
                await api.uploadToPresignedUrl(doc.uploadUrl, doc.uploadFields, file)
                await api.completeDocumentUpload(doc.id)
                return {
                  name: file.name,
                  size: `${(file.size / 1024).toFixed(0)} KB`,
                  documentId: doc.id,
                  uploadStatus: 'ready' as const,
                }
              })
            )
            return results
          }}
        />
      )
  }
}
